/* ============================================================================
   search.js - the fuzzy product finder behind the header search button.

   Why this exists at all: Shopify's storefront search and its predictive search
   endpoint both want the query to start the word it matches, and neither
   tolerates a typo. "geisele" returns nothing for Geissele, "grip" misses a
   product called "Grips", and a part number typed with the dash in the wrong
   place returns nothing. So this fetches the whole published catalogue once, as
   JSON, and scores it in the browser. Forty-four products is about 12KB and one
   request, made the first time the drawer is opened and never again.

   The full-page /search results (sections/main-search.liquid) stay exactly as
   they were and still cover articles and pages. This drawer is products only,
   and hands the query over to that page when the shopper wants everything.

   DOM contract (markup lives in sections/search-drawer.liquid + header.liquid):
     [data-search-toggle]   header button; opens the drawer
     #search-drawer-root    stable wrapper, carrying three URLs and a word:
                            data-search-index  the JSON catalogue to fetch
                            data-search-page   routes.search_url
                            data-search-all    the all-products collection
                            data-search-sold-out  Theme settings > Words > Sold out
       [data-search-scrim]  backdrop; click closes
       [data-search-panel]  the sliding panel
       [data-search-close]  close button
       [data-search-form]   real GET form to /search; works with JS off
       [data-search-input]  the query field
       [data-search-status] aria-live region for the result count
       [data-search-results] rows are written here
       [data-search-hint]   the "spelling need not be exact" line; hidden once
                            there is anything in the results area
   ========================================================================== */
(function () {
  "use strict";

  // The theme's one seam for the globals an asset cannot fake for itself. See
  // the block at the top of assets/cart.js for why it is a global rather than an
  // argument. This file needs exactly one thing from it: the catalogue fetch,
  // which is what makes the ranking below reachable from a test at all.
  var env = window.DCEnv || {};
  var http = env.fetch || function (u, o) { return window.fetch(u, o); };

  var MAX_ROWS = 7;

  function root() { return document.getElementById("search-drawer-root"); }
  function q(sel) { var r = root(); return r && r.querySelector(sel); }
  function panel() { return q("[data-search-panel]"); }
  function scrim() { return q("[data-search-scrim]"); }
  function input() { return q("[data-search-input]"); }
  function results() { return q("[data-search-results]"); }

  /* The section hands over routes.search_url and the all-products URL rather
     than letting this file assume /search and /collections/all. Those are the
     defaults today, but a market or locale prefix would move both, and the
     links built down in render() would be the only part of the drawer that
     did not follow. The fallbacks are there for a page rendering this file
     without the section, which should not happen and should not crash. */
  function routeTo(name, fallback) {
    var r = root();
    return (r && r.getAttribute("data-search-" + name)) || fallback;
  }

  /* Theme settings > Words > Sold out, handed over on the same wrapper so a row
     says what the product cards say. */
  function soldOutWord() {
    var r = root();
    return (r && r.getAttribute("data-search-sold-out")) || "Sold Out";
  }

  function searchPageUrl(query) {
    return routeTo("page", "/search") + "?q=" + encodeURIComponent(query);
  }

  /* ---- matching ------------------------------------------------------------
     Every word of the query is scored against every word of a product's
     searchable text and the best hit wins. A word counts as a hit if it is
     exact, a prefix, contained, within a small edit distance, or present as an
     ordered subsequence, in that order of preference.

     A product must hit on EVERY word of the query, so "magpul grip" narrows
     instead of returning everything Magpul make.
     ---------------------------------------------------------------------- */

  var EXACT = 100;
  var PREFIX = 78;
  var STEM = 72;
  var CONTAINS = 46;
  var FUZZY = 58;
  var FUZZY_PENALTY = 16;
  var LONG_PREFIX = 40;
  var SUBSEQ = 18;

  /* Which fields are worth how much. A title hit is the real thing; a tag hit
     is a hint. SKU sits high because someone typing a part number means it. */
  var FIELDS = [
    ["title", 1],
    ["sku", 0.9],
    ["vendor", 0.75],
    ["type", 0.6],
    ["tags", 0.5],
  ];

  /* Lowercase, drop accents, and collapse everything that is not a letter or a
     digit to one space. Both sides go through this, so "5.11" and "5-11" and
     "5 11" are the same query, and so is "B5-GRP1457" against "B5GRP1457". */
  function normalise(value) {
    return String(value == null ? "" : value)
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, " ")
      .trim();
  }

  /* How many edits a word may be out by. Under four letters it gets none: at
     that length nearly everything is one edit from everything else, and "cap"
     would drag in "cup" and "cab" ahead of the thing that was actually typed. */
  function budget(length) {
    if (length < 4) return 0;
    if (length < 7) return 1;
    return 2;
  }

  /* Every form of a word worth treating as the same word. Enough to make a
     singular query find a plural product name and the other way round: "knife"
     has to find "Knives", "optic" has to find "Optics". A real stemmer is far
     more than this needs, and the candidate-list shape means an awkward pair
     like gloves/glove and knives/knife can both be right without one rule
     having to cover both. */
  function stems(word) {
    var out = [word];
    if (word.length > 3 && /[^s]s$/.test(word)) out.push(word.slice(0, -1));
    if (word.length > 4 && /es$/.test(word)) out.push(word.slice(0, -2));
    if (word.length > 4 && /ves$/.test(word)) out.push(word.slice(0, -3) + "fe");
    if (word.length > 4 && /ies$/.test(word)) out.push(word.slice(0, -3) + "y");
    return out;
  }

  function sameStem(token, word) {
    var a = stems(token);
    var b = stems(word);
    for (var i = 0; i < a.length; i++) {
      for (var j = 0; j < b.length; j++) {
        if (a[i] === b[j]) return true;
      }
    }
    return false;
  }

  /* A long agreeing start with a wrong ending. "streamlite" is three edits from
     "streamlight", past any typo budget worth allowing, but nobody typing it
     meant anything else. Most of the token has to agree, and five characters is
     the floor, so this never fires on the short tokens where a shared start
     means nothing. */
  function sharedPrefix(token, word) {
    var i = 0;
    while (i < token.length && i < word.length && token.charCodeAt(i) === word.charCodeAt(i)) i++;
    if (i < 5 || i < token.length * 0.6) return 0;
    return LONG_PREFIX * (i / Math.max(token.length, word.length));
  }

  /* Levenshtein over two rows, abandoning the moment the whole current row is
     further than `max`. This is the hot path (once per query word per product
     word), so the bail matters more than the elegance. */
  function editDistance(a, b, max) {
    if (a === b) return 0;
    if (Math.abs(a.length - b.length) > max) return max + 1;

    var prev = [];
    var curr = [];
    var i, j;
    for (j = 0; j <= b.length; j++) prev[j] = j;

    for (i = 1; i <= a.length; i++) {
      curr[0] = i;
      var rowBest = i;
      for (j = 1; j <= b.length; j++) {
        var cost = a.charCodeAt(i - 1) === b.charCodeAt(j - 1) ? 0 : 1;
        curr[j] = Math.min(prev[j] + 1, curr[j - 1] + 1, prev[j - 1] + cost);
        if (curr[j] < rowBest) rowBest = curr[j];
      }
      if (rowBest > max) return max + 1;
      for (j = 0; j <= b.length; j++) prev[j] = curr[j];
    }
    return prev[b.length];
  }

  /* The typo and the plural are two separate problems, and one query can carry
     both at once. "knfe" is four edits away from "knives", which is hopeless, but
     one edit from the "knife" that stems() derives from it. Comparing every stem
     of the token against every stem of the word costs a handful of extra passes
     over words that are a dozen characters long, and is what lets a misspelled
     singular find a listing whose title is plural. */
  function nearestStem(token, word, max) {
    var a = stems(token);
    var b = stems(word);
    var best = max + 1;
    for (var i = 0; i < a.length; i++) {
      for (var j = 0; j < b.length; j++) {
        var distance = editDistance(a[i], b[j], max);
        if (distance < best) best = distance;
        if (best === 0) return 0;
      }
    }
    return best;
  }

  /* Are the token's letters present, in order, inside the word? This is what
     catches "gsl" for "geissele". The score falls away as the match spreads
     out, so letters scattered across a long word never beat a real word hit. */
  function subsequence(token, word) {
    var i = 0;
    var first = -1;
    var last = -1;
    for (var j = 0; j < word.length && i < token.length; j++) {
      if (word.charCodeAt(j) === token.charCodeAt(i)) {
        if (first < 0) first = j;
        last = j;
        i++;
      }
    }
    if (i < token.length) return 0;
    return SUBSEQ * (token.length / (last - first + 1));
  }

  function scoreWord(token, word) {
    if (!word) return 0;
    if (token === word) return EXACT;
    if (word.indexOf(token) === 0) return PREFIX;
    // Everything below here is only trustworthy from three letters up.
    if (token.length < 3) return 0;
    if (sameStem(token, word)) return STEM;
    if (word.indexOf(token) > 0) return CONTAINS;

    var max = budget(token.length);
    if (max > 0) {
      var distance = nearestStem(token, word, max);
      if (distance <= max) return FUZZY - distance * FUZZY_PENALTY;
    }

    // Both of these are weak signals, so take whichever is less weak rather
    // than letting the order they are tried in decide.
    return Math.max(sharedPrefix(token, word), subsequence(token, word));
  }

  function scoreField(token, text) {
    if (!text) return 0;
    var words = text.split(" ");
    var best = 0;
    for (var i = 0; i < words.length; i++) {
      var score = scoreWord(token, words[i]);
      if (score > best) best = score;
    }
    // A token typed across a word boundary ("edcpouch") still deserves a hit,
    // just not a confident one.
    if (best === 0 && token.length >= 4 && text.replace(/ /g, "").indexOf(token) >= 0) {
      best = CONTAINS * 0.6;
    }
    return best;
  }

  function scoreProduct(tokens, item) {
    var total = 0;
    for (var t = 0; t < tokens.length; t++) {
      var best = 0;
      for (var f = 0; f < FIELDS.length; f++) {
        var score = scoreField(tokens[t], item.hay[FIELDS[f][0]]) * FIELDS[f][1];
        if (score > best) best = score;
      }
      if (best === 0) return 0;
      total += best;
    }
    return total;
  }

  function rank(query, items, limit) {
    var needle = normalise(query);
    if (!needle) return [];
    var tokens = needle.split(" ");
    var hits = [];

    for (var i = 0; i < items.length; i++) {
      var score = scoreProduct(tokens, items[i]);
      if (score <= 0) continue;
      // The whole query sitting in the title, in order, beats the same words
      // scattered across a vendor and a product type.
      if (items[i].hay.title.indexOf(needle) >= 0) score += 60;
      // Sold-out products still show. They just lose to anything buyable.
      if (!items[i].available) score -= 25;
      hits.push({ item: items[i], score: score, order: i });
    }

    hits.sort(function (a, b) {
      if (b.score !== a.score) return b.score - a.score;
      // Shorter title first, which puts the specific product ahead of the
      // bundle whose name happens to mention it.
      var byLength = a.item.hay.title.length - b.item.hay.title.length;
      return byLength !== 0 ? byLength : a.order - b.order;
    });

    return hits.slice(0, limit || MAX_ROWS).map(function (hit) { return hit.item; });
  }

  /* ---- the index ---------------------------------------------------------- */

  var index = null;
  var loading = null;

  function prepare(products) {
    return products.map(function (product) {
      return {
        title: product.title,
        url: product.url,
        vendor: product.vendor,
        type: product.type,
        price: product.price,
        image: product.image,
        available: product.available !== false,
        hay: {
          title: normalise(product.title),
          sku: normalise(product.sku),
          vendor: normalise(product.vendor),
          type: normalise(product.type),
          tags: normalise(product.tags),
        },
      };
    });
  }

  /* Exposed so the ranking can be exercised outside a browser, and so it can be
     poked at in the console when a search returns something surprising. Nothing
     in the theme reads it.

     It takes the catalogue in the shape templates/collection.search-index.liquid
     emits, not the shape prepare() turns it into. That is the difference between
     a caller having to learn `hay` - five pre-normalised fields, a private
     detail of the scorer - and a caller having to know nothing at all. It also
     means prepare() is exercised rather than reimplemented by whoever calls
     this, which is the half of the ranking that a typo in a field name breaks. */
  window.dcSearchRank = function (query, products, limit) {
    return rank(query, prepare(products), limit);
  };

  /* One fetch, on first open, following `next` if the catalogue ever outgrows a
     single page. Concurrent callers share the same promise. */
  function loadIndex() {
    if (index) return Promise.resolve(index);
    if (loading) return loading;

    var r = root();
    var url = r && r.getAttribute("data-search-index");
    if (!url) return Promise.reject(new Error("no index url"));

    var collected = [];
    function page(next) {
      // The header is the theme's usual one for a fetch of a rendered template,
      // and it is deliberately not `Accept: application/json`. Asking a Shopify
      // storefront URL for JSON makes it go looking for a JSON API route rather
      // than rendering the template at that path, and there is no such route
      // here, so the polite version of this line returns an empty 404 while the
      // impolite one returns the file. It cost an afternoon once already.
      return http(next, { headers: { "X-Requested-With": "XMLHttpRequest" } })
        .then(function (response) {
          if (!response.ok) throw new Error("index " + response.status);
          return response.json();
        })
        .then(function (body) {
          // A body that came back without its array is a broken template, not an
          // empty catalogue. Saying so sends the shopper to the results page,
          // where Shopify's own search still works. Treating it as empty would
          // instead tell them, calmly and repeatedly, that we stock nothing.
          if (!Array.isArray(body.products)) throw new Error("index has no products");
          collected = collected.concat(body.products);
          return body.next ? page(body.next) : collected;
        });
    }

    loading = page(url)
      .then(function (products) {
        index = prepare(products);
        loading = null;
        return index;
      })
      .catch(function (error) {
        loading = null;
        throw error;
      });

    return loading;
  }

  /* ---- rendering ---------------------------------------------------------- */

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  function row(item) {
    var link = el("a", "group flex items-center gap-3 rounded-md border border-line bg-surface p-2.5 transition-colors hover:border-line-strong");
    link.href = item.url;
    link.setAttribute("data-search-row", "");

    var thumb = el("span", "relative grid h-14 w-14 shrink-0 place-items-center overflow-hidden rounded-md border border-line bg-surface-2");
    if (item.image) {
      var img = el("img", "absolute inset-0 h-full w-full object-cover");
      img.src = item.image;
      img.alt = "";
      img.width = 56;
      img.height = 56;
      img.loading = "lazy";
      thumb.appendChild(img);
    } else {
      thumb.appendChild(el("span", "font-display text-lg text-line-strong", item.title.slice(0, 2).toUpperCase()));
    }
    link.appendChild(thumb);

    var middle = el("span", "min-w-0 flex-1");
    var meta = [item.vendor, item.type].filter(Boolean).join(" · ");
    if (meta) middle.appendChild(el("span", "block truncate font-mono text-[0.6rem] uppercase tracking-wide text-muted", meta));
    middle.appendChild(el("span", "block truncate font-display text-sm font-semibold uppercase leading-tight tracking-wide text-bone transition-colors group-hover:text-chalk", item.title));
    link.appendChild(middle);

    var right = el("span", "shrink-0 text-right");
    // The index writes the Words box as it was typed, so the capitals the cards
    // print it in come from here, like the title's.
    right.appendChild(el("span", "block font-display text-sm font-semibold uppercase tabular-nums text-bone", item.price));
    if (!item.available) {
      right.appendChild(el("span", "block font-mono text-[0.6rem] uppercase tracking-wider text-muted", soldOutWord()));
    }
    link.appendChild(right);

    return link;
  }

  function setStatus(text) {
    var node = q("[data-search-status]");
    if (node) node.textContent = text;
  }

  function clear() {
    var box = results();
    if (box) box.textContent = "";
    var hint = q("[data-search-hint]");
    if (hint) hint.hidden = false;
    setStatus("");
  }

  function render(query, items) {
    var box = results();
    if (!box) return;
    box.textContent = "";

    var hint = q("[data-search-hint]");
    if (hint) hint.hidden = true;

    if (items.length === 0) {
      var empty = el("div", "rounded-md border border-line bg-surface px-4 py-6 text-center");
      empty.appendChild(el("p", "text-sm text-bone-dim", "Nothing matches that."));
      var all = el("a", "btn-outline mt-4 inline-flex px-5 py-2.5 text-xs", "Browse all gear");
      all.href = routeTo("all", "/collections/all");
      empty.appendChild(all);
      box.appendChild(empty);
      setStatus("No matches");
      return;
    }

    var list = el("div", "flex flex-col gap-2");
    items.forEach(function (item) { list.appendChild(row(item)); });
    box.appendChild(list);

    var more = el("a", "btn-outline mt-4 flex w-full items-center justify-center px-5 py-3 text-sm");
    more.href = searchPageUrl(query);
    more.style.borderColor = "currentColor";
    more.textContent = (("All results →"));
    box.appendChild(more);

    setStatus(items.length === 1 ? "1 result" : items.length + " results");
  }

  function failed(query) {
    var box = results();
    if (!box) return;
    box.textContent = "";
    var hint = q("[data-search-hint]");
    if (hint) hint.hidden = true;

    // The index did not load, so there is nothing to match against here. Hand
    // the query to the page that does its own searching rather than sit there
    // looking broken.
    var fallback = el("div", "rounded-md border border-line bg-surface px-4 py-6 text-center");
    fallback.appendChild(el("p", "text-sm text-bone-dim", "Live results are unavailable right now."));
    var go = el("a", "btn-outline mt-4 inline-flex px-5 py-2.5 text-xs", "Search the store");
    go.href = searchPageUrl(query);
    fallback.appendChild(go);
    box.appendChild(fallback);
    setStatus("Live results unavailable");
  }

  /* ---- searching --------------------------------------------------------- */

  var current = "";

  function search() {
    var field = input();
    if (!field) return;
    var query = field.value;
    current = query;

    if (!normalise(query)) return clear();

    loadIndex().then(
      function (items) {
        // The field may have moved on while the index was in flight.
        if (current !== query) return;
        render(query, rank(query, items, MAX_ROWS));
      },
      function () {
        if (current === query) failed(query);
      },
    );
  }

  /* ---- the drawer -------------------------------------------------------- */

  var lastFocused = null;

  /* Same trap as the cart drawer: while this is open every top-level element
     except this section is `inert`, so Tab cannot reach the page behind it and
     assistive tech ignores it. No hand-rolled Tab cycling. */
  // Spared as well as this drawer's own section: whatever holds
  // [data-cart-status], the persistent aria-live region cart.js announces into.
  // It sits inside the cart drawer's section, so inert-ing every sibling took the
  // announcement channel out of the accessibility tree along with the page - and
  // an aria-live region inside an inert subtree is not silent-ish, it is silent.
  // Sparing that section is safe because everything focusable in it is already
  // inert on its own: a closed panel carries the attribute, and the scrim is
  // aria-hidden and pointer-events-none.
  //
  // Written the same way in assets/cart.js, and for the cross-store notice in
  // assets/global.js. They are three copies of one trap over one global resource
  // - document.body.children - and none of them knows the others exist; an asset
  // of its own to share eight lines would cost more than it saves, so the
  // duplication is deliberate and this comment is the link.
  function backgroundEls() {
    var section = root() && root().closest(".shopify-section");
    if (!section) return [];
    var status = document.querySelector("[data-cart-status]");
    return Array.prototype.filter.call(document.body.children, function (node) {
      return node !== section && !(status && node.contains(status));
    });
  }

  function isOpen() {
    var p = panel();
    return !!p && p.classList.contains("translate-y-0");
  }

  function setDrawer(open) {
    var p = panel();
    var s = scrim();
    if (!p || !s) return;

    p.classList.toggle("-translate-y-full", !open);
    p.classList.toggle("translate-y-0", open);
    s.classList.toggle("opacity-0", !open);
    s.classList.toggle("pointer-events-none", !open);

    p.toggleAttribute("inert", !open);
    backgroundEls().forEach(function (node) { node.toggleAttribute("inert", open); });

    var toggle = document.querySelector("[data-search-toggle]");
    if (toggle) toggle.setAttribute("aria-expanded", open ? "true" : "false");
  }

  function openDrawer(origin) {
    if (isOpen()) return;
    lastFocused = origin || document.activeElement;

    // Warm the index while the panel animates in, so the first keystroke has it.
    loadIndex().catch(function () {});

    setDrawer(true);
    var field = input();
    if (field) {
      field.focus();
      field.select();
    }
  }

  function closeDrawer() {
    if (!isOpen()) return;
    setDrawer(false);
    if (lastFocused && document.contains(lastFocused) && typeof lastFocused.focus === "function") {
      lastFocused.focus();
    }
    lastFocused = null;
  }

  /* ---- wiring ------------------------------------------------------------ */

  document.addEventListener("click", function (e) {
    var toggle = e.target.closest("[data-search-toggle]");
    if (toggle) {
      e.preventDefault();
      if (isOpen()) closeDrawer();
      else openDrawer(toggle);
      return;
    }
    if (!root()) return;
    if (e.target.closest("[data-search-close]") || e.target === scrim()) {
      e.preventDefault();
      closeDrawer();
    }
  });

  document.addEventListener("input", function (e) {
    if (e.target.closest("[data-search-input]")) search();
  });

  /* Enter on the field follows the top row rather than submitting to /search:
     the row is the answer the shopper is looking at. Arrow keys walk the rows,
     and once one is focused Enter is the browser's own link activation. */
  document.addEventListener("keydown", function (e) {
    if (e.key === "Escape" && isOpen()) {
      e.preventDefault();
      closeDrawer();
      return;
    }
    if (!isOpen()) return;

    var rows = Array.prototype.slice.call(root().querySelectorAll("[data-search-row]"));
    if (rows.length === 0) return;

    var onField = !!e.target.closest("[data-search-input]");
    var at = rows.indexOf(e.target);

    if (e.key === "ArrowDown") {
      e.preventDefault();
      rows[Math.min(at + 1, rows.length - 1)].focus();
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      if (at <= 0) {
        var field = input();
        if (field) field.focus();
      } else {
        rows[at - 1].focus();
      }
      return;
    }
    if (e.key === "Enter" && onField) {
      e.preventDefault();
      var form = e.target.closest("form"); if (form) form.requestSubmit();
    }
  });

  /* Submitting means leaving for /search, which happens when Enter is pressed
     with nothing matched. Close on the way out so the drawer is not still open
     underneath if the browser restores this page from cache. */
  document.addEventListener("submit", function (e) {
    if (e.target.closest("[data-search-form]")) closeDrawer();
  });
})();
